import React, { useMemo, useState, useEffect } from "react";
import { Button, Checkbox, Drawer, TreeSelect as AntdTreeSelect } from "antd";
import { CheckOutlined, CloseCircleFilled, DownOutlined, RightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { getNodeText, isSameValue, toArray } from "../ResponsivePicker/utils";
import "../ResponsivePicker/style.css";

const normalizeTree = (nodes = []) =>
  nodes.map((node) => ({
    value: node.value ?? node.key,
    label: node.title ?? node.label,
    disabled: node.disabled,
    children: normalizeTree(node.children || []),
    raw: node,
  }));

const flattenTree = (nodes, expandedKeys, level = 0) => {
  const rows = [];
  nodes.forEach((node) => {
    rows.push({ ...node, level });
    if (node.children?.length && expandedKeys.includes(String(node.value))) {
      rows.push(...flattenTree(node.children, expandedKeys, level + 1));
    }
  });
  return rows;
};

const findLabels = (nodes, values, labels = []) => {
  nodes.forEach((node) => {
    if (values.some((value) => isSameValue(value, node.value))) labels.push(getNodeText(node.label));
    if (node.children?.length) findLabels(node.children, values, labels);
  });
  return labels;
};

const ResponsiveTreeSelect = (props) => {
  const {
    treeData = [],
    value,
    defaultValue,
    onChange,
    placeholder = "Please select",
    disabled,
    allowClear,
    multiple,
    treeCheckable,
    treeDefaultExpandAll,
    className,
    style,
    ...rest
  } = props;
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const isMultiple = multiple || treeCheckable;
  const controlled = value !== undefined;
  const [innerValue, setInnerValue] = useState(defaultValue);
  const currentValue = controlled ? value : innerValue;
  const [open, setOpen] = useState(false);
  const normalizedTree = useMemo(() => normalizeTree(treeData), [treeData]);
  const allKeys = useMemo(() => {
    const keys = [];
    const walk = (nodes) => nodes.forEach((node) => {
      keys.push(String(node.value));
      if (node.children?.length) walk(node.children);
    });
    walk(normalizedTree);
    return keys;
  }, [normalizedTree]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [draftValue, setDraftValue] = useState(currentValue);

  useEffect(() => {
    if (open) {
      setDraftValue(currentValue);
      if (treeDefaultExpandAll) setExpandedKeys(allKeys);
    }
  }, [allKeys, currentValue, open, treeDefaultExpandAll]);

  if (isPC) {
    return (
      <AntdTreeSelect
        {...rest}
        treeData={treeData}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        allowClear={allowClear}
        multiple={multiple}
        treeCheckable={treeCheckable}
        treeDefaultExpandAll={treeDefaultExpandAll}
        className={className}
        style={style}
      />
    );
  }

  const emitChange = (nextValue, node) => {
    if (!controlled) setInnerValue(nextValue);
    onChange?.(nextValue, undefined, node);
  };
  const currentValues = toArray(currentValue);
  const draftValues = toArray(draftValue);
  const labels = findLabels(normalizedTree, currentValues);
  const displayValue = labels.join(", ");
  const rows = flattenTree(normalizedTree, expandedKeys);

  const toggleExpand = (event, node) => {
    event.stopPropagation();
    const key = String(node.value);
    setExpandedKeys((prev) => prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]);
  };

  const toggleNode = (node) => {
    if (node.disabled) return;
    if (!isMultiple) {
      emitChange(node.value, node.raw);
      setOpen(false);
      return;
    }
    const exists = draftValues.some((value) => isSameValue(value, node.value));
    const next = exists
      ? draftValues.filter((value) => !isSameValue(value, node.value))
      : [...draftValues, node.value];
    setDraftValue(next);
  };

  const clearValue = (event) => {
    event.stopPropagation();
    emitChange(isMultiple ? [] : undefined, undefined);
  };

  return (
    <>
      <div
        className={`responsive-picker-trigger${disabled ? " responsive-picker-trigger-disabled" : ""} ${className || ""}`}
        style={style}
        onClick={() => !disabled && setOpen(true)}
      >
        <span className={`responsive-picker-trigger-value${displayValue ? "" : " responsive-picker-trigger-placeholder"}`}>
          {displayValue || placeholder}
        </span>
        <span className="responsive-picker-trigger-icons">
          {allowClear && displayValue && !disabled ? <CloseCircleFilled className="responsive-picker-clear" onClick={clearValue} /> : null}
          <DownOutlined />
        </span>
      </div>
      <Drawer
        open={open}
        title={placeholder}
        placement="bottom"
        height="76vh"
        className="responsive-picker-drawer"
        onClose={() => setOpen(false)}
        destroyOnClose
      >
        <div className="responsive-picker-body">
          {rows.map((node) => {
            const checked = (isMultiple ? draftValues : currentValues).some((value) => isSameValue(value, node.value));
            const hasChildren = node.children?.length > 0;
            return (
              <div
                key={String(node.value)}
                className={`responsive-tree-row${node.disabled ? " responsive-picker-option-disabled" : ""}`}
                style={{ paddingLeft: 8 + node.level * 18 }}
                onClick={() => toggleNode(node)}
              >
                <span className="responsive-tree-toggle" onClick={(event) => hasChildren && toggleExpand(event, node)}>
                  {hasChildren ? <RightOutlined rotate={expandedKeys.includes(String(node.value)) ? 90 : 0} /> : null}
                </span>
                {isMultiple ? <Checkbox checked={checked} disabled={node.disabled} /> : null}
                <span className="responsive-tree-label">{node.label}</span>
                {!isMultiple && checked ? <CheckOutlined className="responsive-picker-option-check" /> : null}
              </div>
            );
          })}
        </div>
        {isMultiple && (
          <div className="responsive-picker-footer">
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              onClick={() => {
                emitChange(draftValues, undefined);
                setOpen(false);
              }}
            >
              Confirm
            </Button>
          </div>
        )}
      </Drawer>
    </>
  );
};

ResponsiveTreeSelect.TreeNode = AntdTreeSelect.TreeNode;
ResponsiveTreeSelect.SHOW_PARENT = AntdTreeSelect.SHOW_PARENT;
ResponsiveTreeSelect.SHOW_CHILD = AntdTreeSelect.SHOW_CHILD;
ResponsiveTreeSelect.SHOW_ALL = AntdTreeSelect.SHOW_ALL;

export default ResponsiveTreeSelect;
